import { useQuery } from "@tanstack/react-query";
import { useApi } from "../../api/ApiProvider";
import { useOrg } from "../OrgContext";

/** Ids a resolver, agrupados por tipo. Cada lista es opcional. */
export interface IdsAResolver {
  products?: (string | null | undefined)[];
  presentations?: (string | null | undefined)[];
  thirdParties?: (string | null | undefined)[];
}

function limpiar(ids: (string | null | undefined)[] | undefined): string[] {
  if (!ids) {
    return [];
  }
  return [...new Set(ids.filter((id): id is string => typeof id === "string" && id.length > 0))].sort();
}

/**
 * Resuelve, en una sola llamada al backend (`/catalog/name-lookup`) y con caché, los nombres
 * reales de un conjunto de identificadores de producto, presentación y tercero. Pensado para que
 * una pantalla pida de golpe todos los ids que va a pintar y `IdLegible` muestre el nombre en vez
 * del UUID. Devuelve funciones de acceso `producto(id)`, `presentacion(id)`, `tercero(id)`.
 */
export function useNombres(ids: IdsAResolver) {
  const api = useApi();
  const { tenantId } = useOrg();

  const products = limpiar(ids.products);
  const presentations = limpiar(ids.presentations);
  const thirdParties = limpiar(ids.thirdParties);
  const total = products.length + presentations.length + thirdParties.length;

  const query = useQuery({
    queryKey: ["name-lookup", tenantId, products, presentations, thirdParties],
    enabled: total > 0,
    staleTime: 5 * 60_000,
    queryFn: async () => {
      const { data } = await api.GET("/api/v1/tenants/{tenantId}/catalog/name-lookup", {
        params: {
          path: { tenantId },
          query: {
            ...(products.length ? { products } : {}),
            ...(presentations.length ? { presentations } : {}),
            ...(thirdParties.length ? { thirdParties } : {}),
          },
        },
      });
      return data ?? {};
    },
  });

  const busca = (mapa: Record<string, string> | undefined, id: string | null | undefined) =>
    id ? mapa?.[id] : undefined;

  return {
    producto: (id: string | null | undefined) => busca(query.data?.products, id),
    presentacion: (id: string | null | undefined) => busca(query.data?.presentations, id),
    tercero: (id: string | null | undefined) => busca(query.data?.thirdParties, id),
  };
}
